import { Button, ButtonIcon, Flex, Select } from '@backstage/ui';
import { RiAddLine, RiDeleteBinLine } from '@remixicon/react';
import {
  ANY_ANNOTATION_VALUE_LABEL,
  EMPTY_ANNOTATION_FILTER,
  type AnnotationFilter,
} from './scope-filter-model';
import { selectedKey, selectedKeys, toOptions } from '../select-helpers';

export type AnnotationFilterFieldsProps = {
  mode: 'include' | 'exclude';
  /** Annotation values present in the catalog, keyed by annotation key. */
  options: Record<string, string[]>;
  values: AnnotationFilter[];
  onChange: (values: AnnotationFilter[]) => void;
  isDisabled?: boolean;
};

const DESCRIPTIONS: Record<'include' | 'exclude', string> = {
  include: 'Only entities carrying the annotation are evaluated.',
  exclude: 'Entities carrying the annotation are excluded.',
};

/**
 * Annotation narrowing rows for a scope section. Each row is one annotation
 * key; rows are ANDed together and the values within a row are ORed.
 */
export const AnnotationFilterFields = ({
  mode,
  options,
  values,
  onChange,
  isDisabled = false,
}: AnnotationFilterFieldsProps) => {
  const rows = values.length ? values : [EMPTY_ANNOTATION_FILTER];
  const usedKeys = new Set(rows.map(annotation => annotation.key));
  const hasUnusedKey = Object.keys(options).some(key => !usedKeys.has(key));

  const replaceRow = (index: number, annotation: AnnotationFilter) =>
    onChange(
      rows.map((row, rowIndex) => (rowIndex === index ? annotation : row)),
    );

  const keyOptionsFor = (currentKey: string) =>
    toOptions(
      Object.keys(options).filter(
        key => key === currentKey || !usedKeys.has(key),
      ),
    );

  return (
    <Flex direction="column" gap="3">
      {rows.map((annotation, index) => (
        <Flex key={annotation.key || 'unset'} gap="2" align="end">
          <Select
            label="Annotation"
            placeholder="Select an annotation"
            description={DESCRIPTIONS[mode]}
            search
            options={keyOptionsFor(annotation.key)}
            value={annotation.key}
            isDisabled={isDisabled}
            onChange={keys =>
              replaceRow(index, { key: selectedKey(keys) ?? '', values: [] })
            }
            style={{ flex: 2, minWidth: 0 }}
          />
          <Select
            label="Value"
            placeholder={ANY_ANNOTATION_VALUE_LABEL}
            description="Leave the value empty to match any value."
            selectionMode="multiple"
            search
            options={toOptions(options[annotation.key] ?? [])}
            value={annotation.values}
            isDisabled={isDisabled || !annotation.key}
            onChange={keys =>
              replaceRow(index, { ...annotation, values: selectedKeys(keys) })
            }
            style={{ flex: 2, minWidth: 0 }}
          />
          <ButtonIcon
            icon={<RiDeleteBinLine />}
            aria-label="Remove annotation filter"
            variant="tertiary"
            isDisabled={isDisabled || (rows.length === 1 && !annotation.key)}
            onPress={() =>
              onChange(rows.filter((_, rowIndex) => rowIndex !== index))
            }
          />
        </Flex>
      ))}

      <Flex>
        <Button
          variant="secondary"
          iconStart={<RiAddLine />}
          isDisabled={isDisabled || usedKeys.has('') || !hasUnusedKey}
          onPress={() => onChange([...rows, EMPTY_ANNOTATION_FILTER])}
        >
          Add annotation
        </Button>
      </Flex>
    </Flex>
  );
};
